import type { PluginDataVersion } from "@manifold/protocol";
import type { PluginDatabase } from "./database.ts";

/**
 * PER-PLUGIN STORAGE — the one place a plugin may keep durable data of its own.
 *
 * A plugin never sees the database. It gets a namespaced key-value ref bound to its
 * manifest id, plus a versioned migration ledger, and that is the whole substrate: two
 * plugins cannot read each other's rows, a purge can erase exactly one plugin's data
 * without knowing anything about its shape, and "which plugin owns this row" is answered by
 * the key's namespace rather than by a comment.
 *
 * The API is PROMISE-RETURNING, for every plugin. Until ADR 0016 it was synchronous on
 * purpose — Bun's SQLite is synchronous, and a promise per read over it bought no
 * concurrency — and that ruling is reversed by ADR 0016 §4 (ratified, R3): an isolated
 * plugin's storage calls cross a process boundary, so they are promises, and two storage
 * contracts — sync in-realm, async isolated — would be two doors onto one concept (docs/CONTRACTS.md §One authoritative implementation) that every plugin author would have to tell apart. One contract, first-party plugins
 * included, is the T2 cost the ADR states plainly: an in-realm plugin pays a promise per
 * read over a synchronous SQLite call, and every handler that reads storage gains an
 * `await`. The in-realm implementation stays synchronous inside and resolves immediately —
 * no queue, no async driver — which is what keeps a chain of awaited storage calls free of
 * interleaving with dispatch (see `PluginMigration`).
 *
 * Values are strings. A plugin that wants structure serializes it (its own schema, its own
 * versioning) exactly as the server's `meta` rows already do: the engine has no business
 * knowing whether a plugin's blob is JSON, and typing this ref as `unknown` would only
 * move a `JSON.parse` from the caller into the floor.
 *
 * Wave 1 ships the ref and the ledger. The domain tables that today live in bespoke
 * SQLite tables owned by floor code (terminal names, machine rows, view state) move onto
 * this ref in the conversion batch — that move is what the version/ledger machinery
 * below exists for, and why it ships before its first real occupant.
 *
 * A refused operation — a reserved or malformed key, an oversize value — REJECTS the
 * returned promise with `PluginStorageError`; nothing here throws synchronously, so a caller
 * has exactly one failure path whether its storage is in-realm or served over an RPC.
 */
export interface PluginStorage {
  /** The manifest id this handle is bound to; every key below is namespaced by it. */
  readonly pluginId: string;
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  /**
   * Atomically writes value only when the key is absent (expected is null) or its stored
   * bytes equal expected. Returns false without changing data on a mismatch. Both strings
   * obey the value limit, and the key obeys the same rules as set.
   */
  compareAndSet(key: string, expected: string | null, value: string): Promise<boolean>;
  delete(key: string): Promise<void>;
  /** Every key this plugin holds, sorted, optionally narrowed to a prefix. */
  keys(prefix?: string): Promise<readonly string[]>;
  /** The data version last stamped by the engine; null when this plugin stored nothing yet. */
  dataVersion(): Promise<PluginDataVersion | null>;
  /** Names of the migrations already applied, sorted — the ledger. */
  appliedMigrations(): Promise<readonly string[]>;
}

/**
 * The engine's half of the same ref. `set`/`compareAndSet`/`delete` on a `PluginStorage` refuse
 * reserved keys, so a plugin cannot forge its own data version or ledger entry; the engine writes
 * those through here instead. `clear` is the purge verb's hands, and `count` is what it would
 * take — the number an uninstall refuses over while it is not zero.
 */
export interface PluginStorageAdmin extends PluginStorage {
  stampDataVersion(version: PluginDataVersion): Promise<void>;
  recordMigration(name: string, applied: number): Promise<void>;
  /** Every row of this plugin, reserved keys included: exactly what `clear` would remove. */
  count(): Promise<number>;
  /** Erases every row of this plugin, reserved keys included, and reports how many went. */
  clear(): Promise<number>;
}

/**
 * Keys are ASCII, bounded, and free of the reserved prefix. The pattern is deliberately
 * generous about interior punctuation (`:` and `.` are how a plugin builds its own
 * sub-namespaces, e.g. `element:abc123`) and strict about the first character, which is what
 * keeps engine-reserved keys unforgeable.
 */
export const PLUGIN_STORAGE_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:@/-]{0,127}$/;

/** Engine-reserved keys start with this; a plugin write naming one is refused. */
export const RESERVED_KEY_PREFIX = "$";

/** The stamped data version of a plugin's stored data. */
export const DATA_VERSION_KEY = `${RESERVED_KEY_PREFIX}version`;

/** One row per applied migration name: the ledger, so a migration runs at most once. */
export const MIGRATION_KEY_PREFIX = `${RESERVED_KEY_PREFIX}migration:`;

/**
 * A value is one string, bounded so a plugin cannot turn the workspace's database into its
 * own object store by accident. A plugin with genuinely large data has a shape problem that
 * a key-value ref should not paper over.
 */
export const MAX_STORAGE_VALUE_BYTES = 64 * 1024;

/**
 * A refused storage operation: an authoring bug, so the promise rejects with it rather than
 * resolving null. The assertions below throw; the storage implementation runs them inside
 * the promise it returns, which is how a throw here becomes a rejection there.
 */
export class PluginStorageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PluginStorageError";
  }
}

/** Validates a key a PLUGIN supplied. Reserved keys are the engine's alone. */
export function assertStorageKey(key: string): void {
  if (key.startsWith(RESERVED_KEY_PREFIX)) {
    throw new PluginStorageError(
      `storage key "${key}" is reserved: keys starting with "${RESERVED_KEY_PREFIX}" belong to the engine`,
    );
  }
  if (!PLUGIN_STORAGE_KEY_PATTERN.test(key)) {
    throw new PluginStorageError(
      `storage key "${key}" is not a valid key (ASCII, 1-128 chars, starting alphanumeric)`,
    );
  }
}

/** Validates a value a plugin supplied. */
export function assertStorageValue(key: string, value: string): void {
  const bytes = Buffer.byteLength(value, "utf8");
  if (bytes > MAX_STORAGE_VALUE_BYTES) {
    throw new PluginStorageError(
      `storage value for "${key}" is ${String(bytes)} bytes, over the ${String(MAX_STORAGE_VALUE_BYTES)}-byte limit`,
    );
  }
}

/** `{ major: 2, minor: 1 }` → `"2.1"`, the stamped on-disk form. */
export function formatDataVersion(version: PluginDataVersion): string {
  return `${String(version.major)}.${String(version.minor)}`;
}

/** The inverse; null for anything that is not two non-negative integers. */
export function parseDataVersion(text: string): PluginDataVersion | null {
  const match = /^(\d{1,9})\.(\d{1,9})$/.exec(text);
  if (match === null) return null;
  return { major: Number(match[1]), minor: Number(match[2]) };
}

/** Total order on data versions: major first, then minor. */
export function compareDataVersion(left: PluginDataVersion, right: PluginDataVersion): number {
  if (left.major !== right.major) return left.major - right.major;
  return left.minor - right.minor;
}

/**
 * ONE NAMED MIGRATION. The name — not the version — is what the ledger records, because a
 * name is stable under rebase, reordering and version renumbering, and "did this exact
 * transformation already run?" is the only question a ledger has to answer.
 *
 * `to` is the data version this migration produces, so a chain can be planned without
 * running anything, and `assembleRoster` can refuse a migration claiming to reach past the
 * version its own code declares.
 *
 * Promise-returning because storage may cross the guest IPC boundary (ADR 0016 §4).
 * The host drains this plugin's admitted dispatches and refuses new ones during an update,
 * runs the bounded chain on private storage, then commits data, ledger and version together.
 * No SQLite transaction spans a callback await. Failure discards the whole chain, and the
 * callback's storage handle expires: late work cannot mutate retained or committed data.
 */
export interface PluginMigration {
  readonly name: string;
  readonly to: PluginDataVersion;
  /**
   * `database` is present exactly when this plugin's manifest declares `database` (ADR 0034
   * §3): the ledger and the version stamp stay in `plugin_kv`, so a plugin has ONE data
   * version and ONE ledger whether its data is keys, rows or both, and a migration creates
   * its tables with ordinary `CREATE TABLE` statements through `database.run`. The parameter
   * is optional so every migration written before the file existed keeps compiling and keeps
   * meaning what it meant; a migration that reaches for it without declaring the file in its
   * manifest gets `undefined`, which is the honest answer.
   */
  migrate(storage: PluginStorage, database?: PluginDatabase): void | Promise<void>;
}

/**
 * What the engine must do about a plugin's stored data before that plugin serves. The
 * ratified Home-Assistant asymmetry, in one pure function:
 *
 * - **minor differences pass in BOTH directions.** A minor bump promises the old code can
 *   still read the new data; that promise is the definition of "minor", and enforcing it
 *   again here would only forbid rollbacks that are safe by construction.
 * - **a major bump forward requires a declared, unapplied migration chain** reaching the
 *   declared major. Absent one, composition refuses (`data_migration_missing`): new code
 *   over old data it cannot read is the corruption this mechanism exists to prevent.
 * - **a major downgrade refuses** (`data_downgrade`). Old code cannot be trusted with newer
 *   data and no migration runs backwards — HA's own rule.
 * - **no declared `dataVersion` means unversioned**: never refuses, never migrates. A plugin
 *   that keeps no durable data must not be made to think about any of this.
 */
export type DataPlan =
  | { readonly kind: "ok"; readonly stamp: PluginDataVersion | null }
  | {
      readonly kind: "migrate";
      readonly run: readonly PluginMigration[];
      readonly stamp: PluginDataVersion;
    }
  | {
      readonly kind: "refused";
      readonly reason: "data_downgrade" | "data_migration_missing";
      readonly detail: string;
    };

export function planDataMigration(input: {
  readonly pluginId: string;
  readonly declared: PluginDataVersion | undefined;
  readonly stored: PluginDataVersion | null;
  readonly applied: ReadonlySet<string>;
  readonly migrations: readonly PluginMigration[];
}): DataPlan {
  const { pluginId, declared, stored, applied, migrations } = input;
  if (declared === undefined) return { kind: "ok", stamp: null };
  if (stored === null) return { kind: "ok", stamp: declared };
  if (stored.major > declared.major) {
    return {
      kind: "refused",
      reason: "data_downgrade",
      detail: `plugin "${pluginId}" stored data at ${formatDataVersion(stored)} but its code declares ${formatDataVersion(declared)}: a major downgrade is refused, no migration runs backwards`,
    };
  }
  if (stored.major === declared.major) return { kind: "ok", stamp: declared };

  const run = [...migrations]
    .filter((migration) => !applied.has(migration.name))
    .filter((migration) => compareDataVersion(migration.to, stored) > 0)
    .sort(
      (left, right) => compareDataVersion(left.to, right.to) || (left.name < right.name ? -1 : 1),
    );
  const reached = run.at(-1)?.to;
  if (reached === undefined || reached.major < declared.major) {
    return {
      kind: "refused",
      reason: "data_migration_missing",
      detail: `plugin "${pluginId}" stored data at ${formatDataVersion(stored)} and its code declares ${formatDataVersion(declared)}, but no unapplied migration reaches major ${String(declared.major)}`,
    };
  }
  return { kind: "migrate", run, stamp: declared };
}
